import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { FilterMode, UseCase } from './constants';

/**
 * Stands in for "no use case" in the picker. Radix rejects an empty string as
 * an item value, and a namespaced sentinel can't collide with a real id.
 */
const ALL_USE_CASES = '__all__';

/** Mirrors the list's group headings, so the filter names what it keeps. */
const FILTER_OPTIONS: ReadonlyArray<{ mode: FilterMode; label: string }> = [
  { mode: 'ALL', label: 'All' },
  { mode: 'CONFIGURED', label: 'Connected' },
  { mode: 'SETUP', label: 'Available' },
];

/**
 * Inset-pill segmented control: a muted track with the active option raised on
 * the card surface. `toggleVariants` styles the "on" state as a flat accent
 * fill, so these override it — `cn` is tailwind-merge, so the later
 * `data-[state=on]:*` utilities replace the defaults rather than stacking.
 */
const FILTER_TRACK_CLASS =
  'shrink-0 h-10 items-center gap-0 rounded-lg bg-muted p-[3px]';

const FILTER_OPTION_CLASS =
  'h-full flex-none rounded-md px-3 text-xs font-medium text-muted-foreground ' +
  'hover:bg-transparent hover:text-foreground ' +
  'data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm';

/**
 * Text field with a leading search affordance and a clear button, shared by
 * the catalog header and the per-provider tool filter.
 */
export const SearchInput: React.FC<
  Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange'> & {
    value: string;
    onChange: (value: string) => void;
    /** Accessible name for the clear button, e.g. "Clear search". */
    clearLabel: string;
    className?: string;
    inputRef?: React.RefObject<HTMLInputElement>;
  }
> = ({ value, onChange, clearLabel, className, inputRef, ...inputProps }) => (
  <div className="relative w-full">
    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60 pointer-events-none" />
    <Input
      ref={inputRef}
      className={cn('pl-9 pr-8 bg-card border-border/60', className)}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      {...inputProps}
    />
    {value.length > 0 && (
      <button
        type="button"
        aria-label={clearLabel}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted/60 transition-colors"
        onClick={() => onChange('')}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

/**
 * A search that collapses to its icon until asked for, matching the resource
 * list toolbar on Automations and Library.
 *
 * Suits a *secondary* filter sitting beside content that is browsable without
 * it. The catalog's own search stays permanently open: it is the primary way
 * into a thousand-plus tools and is focused the moment the modal opens, so
 * hiding it behind a click would cost the fastest path in.
 */
export const CollapsibleSearch: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Accessible name for the collapsed trigger, e.g. "Filter tools". */
  label: string;
  className?: string;
}> = ({ value, onChange, placeholder, label, className }) => {
  const [open, setOpen] = useState(false);

  const collapse = () => {
    onChange('');
    setOpen(false);
  };

  return (
    <div className={cn('flex items-center shrink-0', className)}>
      <button
        type="button"
        aria-label={open ? `Close ${label.toLowerCase()}` : label}
        aria-expanded={open}
        onClick={() => (open ? collapse() : setOpen(true))}
        className={cn(
          'inline-flex items-center justify-center h-8 w-8 rounded-md',
          'text-muted-foreground hover:text-foreground hover:bg-secondary',
          'transition-colors cursor-pointer',
          open && 'text-foreground'
        )}
      >
        <Search className="h-4 w-4" />
      </button>
      {open && (
        <div className="relative flex items-center">
          <Input
            autoFocus
            type="text"
            aria-label={placeholder}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                // This lives inside a modal, unlike the toolbar it mirrors:
                // let Escape through and the modal manager dismisses the whole
                // dialog instead of just closing this field.
                e.stopPropagation();
                collapse();
              }
            }}
            className="h-8 w-40 text-xs px-2 pr-7 !border-none !shadow-none !rounded-none !ring-0 focus-visible:!ring-0 focus-visible:!shadow-none focus-visible:!border-none"
          />
          {value.length > 0 && (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => onChange('')}
              className="absolute right-1 p-1 rounded text-muted-foreground/60 hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Search field, use-case picker and connection filter, spanning both panes of
 * the catalog. Wraps to a second row when the modal is too narrow to hold all
 * three side by side.
 */
export const CatalogHeader: React.FC<{
  query: string;
  onQueryChange: (value: string) => void;
  filter: FilterMode;
  onFilterChange: (mode: FilterMode) => void;
  useCases: UseCase[];
  selectedUseCaseId: string | null;
  onSelectUseCase: (useCaseId: string | null) => void;
  resultCount: number;
  /** Id of the row the arrow keys are currently on, for screen readers. */
  activeDescendantId?: string;
  /** Arrow keys and Enter drive the list from the field; see `ToolConnections`. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}> = ({
  query,
  onQueryChange,
  filter,
  onFilterChange,
  useCases,
  selectedUseCaseId,
  onSelectUseCase,
  resultCount,
  activeDescendantId,
  onKeyDown,
  inputRef,
}) => (
  <div className="shrink-0 flex flex-wrap items-center gap-3 pb-4 border-b border-border/50">
    {/* The field takes the whole first row on a phone, which is what breaks the
        row and sends the picker and the status track to the second. Side by
        side the three shared one line at their minimums — the field pinned at
        its 12rem floor, too narrow for its own placeholder, which came out as
        "Search tools and provide". */}
    <div className="w-full min-w-0 sm:w-auto sm:min-w-[12rem] sm:flex-1">
      <SearchInput
        inputRef={inputRef}
        autoFocus
        role="combobox"
        aria-expanded
        aria-controls="tool-catalog-list"
        aria-activedescendant={activeDescendantId}
        className="h-10 text-sm rounded-lg"
        placeholder="Search tools and providers…"
        clearLabel="Clear search"
        value={query}
        onChange={onQueryChange}
        onKeyDown={onKeyDown}
      />
    </div>

    {/* A picker rather than a chip strip: thirteen categories only fit on one
        row behind a horizontal scroll, which hides most of them. */}
    <Select
      value={selectedUseCaseId ?? ALL_USE_CASES}
      onValueChange={(next) =>
        onSelectUseCase(next === ALL_USE_CASES ? null : next)
      }
    >
      <SelectTrigger
        aria-label="Use case"
        // Shares the second row with the status track on a phone, so it gives
        // width rather than holding 12rem of it.
        className="h-10 min-w-0 flex-1 rounded-lg border-border/60 bg-card sm:w-48 sm:flex-none sm:shrink-0"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_USE_CASES}>All use cases</SelectItem>
        {useCases.map((useCase) => {
          const Icon = useCase.icon;
          return (
            <SelectItem key={useCase.id} value={useCase.id}>
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {useCase.name}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>

    {/* `type="single"` emits '' when the active item is clicked again, which
        would otherwise clear the filter into an invalid mode. */}
    <ToggleGroup
      type="single"
      value={filter}
      onValueChange={(next) => {
        if (next !== '') {
          onFilterChange(next as FilterMode);
        }
      }}
      aria-label="Connection status"
      className={FILTER_TRACK_CLASS}
    >
      {FILTER_OPTIONS.map(({ mode, label }) => (
        <ToggleGroupItem
          key={mode}
          value={mode}
          className={FILTER_OPTION_CLASS}
        >
          {label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>

    <span className="sr-only" aria-live="polite">
      {resultCount} results
    </span>
  </div>
);

/**
 * Shown in place of the catalog list when nothing survives the current
 * search + filter combination. Names whichever constraint is most likely at
 * fault and offers the one-click way out of it.
 */
export const CatalogEmptyState: React.FC<{
  query: string;
  filter: FilterMode;
  useCaseName: string | null;
  onClearSearch: () => void;
  onClearFilters: () => void;
}> = ({ query, filter, useCaseName, onClearSearch, onClearFilters }) => {
  const hasConstraints = filter !== 'ALL' || useCaseName !== null;

  if (query.length > 0) {
    return (
      <EmptyStateShell
        title={`No matches for “${query}”`}
        body={
          hasConstraints
            ? 'Your filters may be hiding results.'
            : 'Try a provider name, or the action you want to run.'
        }
      >
        <EmptyStateAction onClick={onClearSearch}>
          Clear search
        </EmptyStateAction>
        {hasConstraints && (
          <EmptyStateAction onClick={onClearFilters}>
            Reset filters
          </EmptyStateAction>
        )}
      </EmptyStateShell>
    );
  }

  return (
    <EmptyStateShell
      title={
        filter === 'CONFIGURED' ? 'Nothing connected yet' : 'No providers here'
      }
      body={
        filter === 'CONFIGURED'
          ? useCaseName !== null
            ? `No ${useCaseName} provider is connected yet.`
            : 'Connect a provider to see it here.'
          : 'Nothing matches the current filters.'
      }
    >
      {hasConstraints && (
        <EmptyStateAction onClick={onClearFilters}>
          Reset filters
        </EmptyStateAction>
      )}
    </EmptyStateShell>
  );
};

const EmptyStateShell: React.FC<{
  title: string;
  body: string;
  children?: React.ReactNode;
}> = ({ title, body, children }) => (
  <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
    <p className="text-sm font-medium text-foreground">{title}</p>
    <p className="text-xs text-muted-foreground mt-1 max-w-[30ch]">{body}</p>
    <div className="flex items-center gap-3 mt-4">{children}</div>
  </div>
);

const EmptyStateAction: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => (
  <button onClick={onClick} className="text-xs text-primary hover:underline">
    {children}
  </button>
);
